import React from 'react';
import { CheckCircle2, XCircle, ShieldCheck, Award, Zap, DollarSign, Clock } from 'lucide-react';

/**
 * Category-default comparisons. Keyed by category SLUG (matches
 * `product.category`). When a product does not have its own `comparison`
 * object and no ancestor category has one either, we fall back to
 * `UNIVERSAL` — this guarantees the section renders on EVERY PDP,
 * including brand-new categories the founder has not yet configured.
 */
const CATEGORY_DEFAULTS = {
  flooring: {
    alternative_name: 'Traditional Laminate',
    title: 'Dr Tools SPC/LVT vs. Traditional Laminate',
    durability_us: '25+ year residential warranty · scratch-, dent- and water-resistant',
    durability_them: '10-15 years typical · swells and delaminates from moisture',
    installation_us: 'Angle-and-tap click-lock, tolerates ~3 mm sub-floor undulation',
    installation_them: 'Click-lock but demands a flat sub-floor, often extra latex screed',
    price_us: 'Higher upfront, 3× the lifespan — best pounds-per-year in the range',
    price_them: 'Cheap up front but full replacement every decade',
    warranty_us: '25 - 35 years residential (up to lifetime on premium)',
    warranty_them: '10 - 15 years typical, void once exposed to water',
    verdict: 'Traditional laminate is fine for a spare bedroom, but the moment moisture, castors or a dog enter the picture it fails fast. Our SPC/LVT ranges click together just as easily, tolerate spills, and outlast laminate two-to-one — which is why UK fitters spec them by default in kitchens, hallways and rental refurbishments.',
  },
  decking: {
    alternative_name: 'Softwood Decking',
    title: 'Dr Tools Composite Decking vs. Softwood',
    durability_us: 'Capped composite core — fade-, stain- and rot-proof for 25+ years',
    durability_them: 'Splits, warps and greys within 4-6 UK winters without treatment',
    installation_us: 'Hidden clip fixings on grooved boards — cleanest finish, no screw heads',
    installation_them: 'Surface-screwed every 300 mm — expect visible fixings and pilot drilling',
    price_us: 'Higher £/m² up front, but zero re-oiling or sanding for the life of the deck',
    price_them: 'Cheap boards but budget for annual jet-wash + biannual oiling forever',
    warranty_us: 'Up to 30 years against splitting, rot and stain',
    warranty_them: 'None to speak of — treat it right or replace it',
    verdict: "Softwood decking is the classic budget option, but a UK winter is genuinely brutal to it — most builds need re-treating in year two and full replacement by year eight. Our capped composite range gives you the same warm timber look with none of the upkeep and lasts three times longer, which is why it is now the default spec on every trade build we ship.",
  },
  cladding: {
    alternative_name: 'Untreated Softwood Cladding',
    title: 'Dr Tools Cladding vs. Untreated Softwood',
    durability_us: 'A-class or B-class fire ratings · dimensionally stable in freeze-thaw',
    durability_them: 'Cups, splits and grows black mould on north elevations within 2 years',
    installation_us: 'Consistent tongue-and-groove profiles · shim-friendly for uneven walls',
    installation_them: 'Board-to-board variation means every plank needs a plane pass',
    price_us: 'Higher £/m² but stays looking new — no re-oiling or sanding cycle',
    price_them: 'Lowest possible upfront cost — full sand + treat every 2 years afterwards',
    warranty_us: '25 - 40 years depending on material (aluminium longest)',
    warranty_them: 'Zero — expect a facelift every 4-6 years',
    verdict: 'For an exterior facade that faces the British weather, untreated softwood is a false economy. Our WPC, aluminium and modified-timber ranges match the aesthetic, laugh off horizontal rain, and hold their fire rating — so the finished facade still looks new in ten years, not tired in three.',
  },
  building: {
    alternative_name: 'Generic Builders Merchant Stock',
    title: 'Dr Tools Building Materials vs. Generic Merchant Stock',
    durability_us: 'Sourced from spec-graded factories with batch traceability',
    durability_them: 'Mixed batches from cheapest-of-day suppliers — inconsistent tolerances',
    installation_us: 'Consistent dimensions across pallets — no on-site trimming',
    installation_them: 'Board-to-board variation eats labour time on site',
    price_us: 'Trade tier pricing baked in — no haggling to reach a fair rate',
    price_them: 'Sticker price looks lower until you factor in wastage and rework',
    warranty_us: 'Manufacturer warranties honoured directly by Dr Tools',
    warranty_them: 'Warranty pass-through — good luck getting it enforced',
    verdict: "Any merchant can sell you a pallet, but not every merchant stands behind it. We only stock brands whose manufacturers we have vetted personally, and our trade prices are transparent from your first order — so the finished job comes out on-spec and on-budget.",
  },
  bathrooms: {
    alternative_name: 'DIY-Chain Bathroom Fittings',
    title: 'Dr Tools Bathrooms vs. DIY-Chain Fittings',
    durability_us: '316-grade stainless steel and solid-brass internals across the range',
    durability_them: 'Chromed plastic and hollow zinc-alloy — pitting within 12 months',
    installation_us: 'Universal G½" / G¾" fittings — swaps into any UK bathroom without adapters',
    installation_them: 'Non-standard threads mean adapters, PTFE tape and callback plumbers',
    price_us: 'Trade pricing on quality brands — often cheaper than DIY high-street',
    price_them: 'Loss-leader pricing that turns into a replacement bill in year two',
    warranty_us: '10 - 25 year mechanical warranty on taps and mixers',
    warranty_them: '1 - 2 years typical — practically worthless',
    verdict: 'DIY-chain bathroom fittings look identical in the showroom but fail predictably within two years. Our range is the same quality trade plumbers reach for on paid installs — 316-grade stainless, real brass internals — at prices that still beat the DIY chains once you factor in the tap they will not need to replace.',
  },
  tools: {
    alternative_name: 'Grey-Market Trade Tools',
    title: 'Dr Tools UK Trade Tools vs. Grey-Market Imports',
    durability_us: 'Genuine UK-warranty stock — brushless motors, hardened gears',
    durability_them: 'Refurbished or non-UK-spec — batteries degrade faster, gears strip',
    installation_us: 'Every tool ships with UK-plug charger and correct wattage',
    installation_them: 'Continental plugs, mismatched batteries, missing spares',
    price_us: 'Trade tier pricing on manufacturer-warranted stock',
    price_them: 'Lower sticker but warranty is invalid outside origin country',
    warranty_us: 'Full UK-honoured manufacturer warranty (3 - 5 years typical)',
    warranty_them: 'Voided the moment you cross the Channel',
    verdict: "Grey-market tools look identical to the UK-warranted item until the day you actually need the warranty. Every tool we ship is UK-spec, plugged for a UK socket and covered by the manufacturer's UK service network — the price difference disappears the first time your drill quits on-site.",
  },
  decorating: {
    alternative_name: 'Own-Brand Paint & Decorating',
    title: 'Dr Tools Decorating vs. Own-Brand',
    durability_us: 'Full-solids formulations — one-coat coverage on a well-prepped wall',
    durability_them: 'Watered-down solids — two or three coats to hit stated coverage',
    installation_us: 'Consistent viscosity across tins — no thinning to spray',
    installation_them: 'Batch variation means adjusting the sprayer every tin',
    price_us: '£/m² covered comes out cheaper once coats are accounted for',
    price_them: "Cheap sticker price hides the extra tins you'll actually need",
    warranty_us: '15-25 year exterior warranties (Dulux Weathershield etc.)',
    warranty_them: '2-5 years or none',
    verdict: 'Own-brand paint looks cheap until you count the coats. Our stocked ranges (Dulux Trade, Weathershield etc.) actually hit their stated coverage in one coat, saving both material and half the labour bill on any real-sized job.',
  },
};

/** Universal fallback — used when the product AND every parent category
 *  come back empty. Keeps the section on every PDP no matter what. */
const UNIVERSAL_FALLBACK = {
  alternative_name: 'Generic Market Alternative',
  title: 'Dr Tools UK vs. Generic Market Alternative',
  durability_us: 'Spec-graded stock, batch-traceable — chosen by our founder personally',
  durability_them: 'Cheapest-of-day sourcing — quality varies plank-to-plank',
  installation_us: 'Consistent dimensions across pallets — no on-site trimming to fit',
  installation_them: 'Board-to-board variation eats labour time on site',
  price_us: 'Trade tier pricing baked in — no haggling to reach a fair rate',
  price_them: 'Looks cheaper on the sticker, more expensive once wastage lands',
  warranty_us: 'Manufacturer warranties honoured directly by Dr Tools UK',
  warranty_them: 'Warranty pass-through — good luck getting it enforced',
  verdict: 'Every SKU in our catalogue is stocked from our Iver warehouse and hand-picked by our founder — no drop-ship, no unknown factories. That means you can quote a job, know exactly what turns up on the pallet, and stand behind the finished work with a warranty we honour ourselves.',
};

/**
 * Resolve the comparison object for a product:
 *   1. product.comparison  (admin SKU override — partial fields allowed)
 *   2. CATEGORY_DEFAULTS[product.category]
 *   3. UNIVERSAL_FALLBACK
 *
 * When product.comparison is a partial override, we ONLY spread its keys that
 * carry a real value on top of the base — otherwise Pydantic-serialised nulls
 * from the API blank out the category-default rows.
 */
export const resolveComparison = (product) => {
  if (!product) return UNIVERSAL_FALLBACK;
  if (product.comparison && product.comparison.alternative_name) {
    const base = CATEGORY_DEFAULTS[product.category] || UNIVERSAL_FALLBACK;
    // Strip null / undefined / empty-string keys so a partial admin override
    // still inherits the category default for un-supplied fields.
    const override = Object.fromEntries(
      Object.entries(product.comparison).filter(([, v]) => v != null && v !== '')
    );
    const merged = { ...base, ...override };
    // If the admin overrode `alternative_name` but didn't supply a new
    // `title`, drop the base title so <h2> regenerates from the new
    // alternative_name (via the `X vs. Y` fallback in the component).
    if (override.alternative_name && !override.title) {
      delete merged.title;
    }
    return merged;
  }
  return CATEGORY_DEFAULTS[product.category] || UNIVERSAL_FALLBACK;
};

const Row = ({ Icon, label, us, them }) => (
  <tr className="border-t border-neutral-200">
    <td className="py-3 px-4 font-bold text-[#12263A] align-top">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#F46A25]" /> {label}
      </div>
    </td>
    <td className="py-3 px-4 align-top">
      <div className="flex items-start gap-2 text-sm text-neutral-800">
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
        <span>{us}</span>
      </div>
    </td>
    <td className="py-3 px-4 align-top">
      <div className="flex items-start gap-2 text-sm text-neutral-600">
        <XCircle className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5" />
        <span>{them}</span>
      </div>
    </td>
  </tr>
);

/**
 * Expert Comparison section rendered on every PDP.
 *
 * Automated rollout: this component NEVER hides. When there is no data at any
 * level of the resolution chain the UNIVERSAL_FALLBACK renders — so a brand-
 * new admin has a persuasive comparison card the day they list their first
 * product.
 */
export const ExpertComparison = ({ product, brandName = 'Dr Tools UK' }) => {
  const cmp = resolveComparison(product);
  const usLabel = brandName;
  const themLabel = cmp.alternative_name;
  const title = cmp.title || `${usLabel} vs. ${themLabel}`;

  return (
    <section
      className="mt-10 border border-neutral-200 rounded-sm overflow-hidden bg-white"
      data-testid="expert-comparison"
    >
      <div className="bg-[#12263A] text-white px-5 py-4 flex items-center gap-3">
        <div className="bg-[#F46A25] p-2 rounded-sm flex-shrink-0">
          <Award className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Expert Comparison</div>
          <h2 className="text-lg md:text-xl font-black leading-tight">{title}</h2>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-neutral-50">
              <th className="w-[22%] py-3 px-4 text-left text-[11px] uppercase tracking-wider font-black text-neutral-500">Criterion</th>
              <th className="py-3 px-4 text-left text-[11px] uppercase tracking-wider font-black text-[#F46A25]">
                {usLabel}
              </th>
              <th className="py-3 px-4 text-left text-[11px] uppercase tracking-wider font-black text-neutral-500">
                {themLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            <Row Icon={ShieldCheck} label="Durability" us={cmp.durability_us} them={cmp.durability_them} />
            <Row Icon={Zap} label="Installation speed" us={cmp.installation_us} them={cmp.installation_them} />
            <Row Icon={DollarSign} label="Price-to-performance" us={cmp.price_us} them={cmp.price_them} />
            <Row Icon={Clock} label="Warranty" us={cmp.warranty_us} them={cmp.warranty_them} />
          </tbody>
        </table>
      </div>

      <div className="border-t-2 border-[#F46A25] bg-orange-50/40 p-5" data-testid="expert-comparison-verdict">
        <div className="text-[10px] uppercase tracking-wider font-black text-[#F46A25] mb-1">Why choose Dr Tools UK?</div>
        <p className="text-neutral-800 leading-relaxed">{cmp.verdict}</p>
      </div>
    </section>
  );
};

export default ExpertComparison;
